import {
  MergePreviewOverlapItem,
  MergePreviewReminderConflict,
  MergePreviewStopRef,
  MergeTripPreviewResponse,
} from '../types/trip.types';

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function getConflictsContainer(preview: MergeTripPreviewResponse): UnknownRecord | null {
  if (isRecord(preview.conflicts)) {
    return preview.conflicts;
  }
  const alt = preview.conflict;
  return isRecord(alt) ? alt : null;
}

function getCandidate(preview: MergeTripPreviewResponse, key: string): unknown {
  if (key in preview) {
    return preview[key];
  }
  const container = getConflictsContainer(preview);
  return container?.[key];
}

function toStopRef(value: unknown): MergePreviewStopRef | null {
  if (!isRecord(value)) {
    return null;
  }

  if (typeof value.id === 'number') {
    return value as unknown as MergePreviewStopRef;
  }
  if (isRecord(value.stop) && typeof value.stop.id === 'number') {
    return value.stop as unknown as MergePreviewStopRef;
  }
  if (isRecord(value.trip_stop) && typeof value.trip_stop.id === 'number') {
    return value.trip_stop as unknown as MergePreviewStopRef;
  }

  return null;
}

function toStopGroup(value: unknown): MergePreviewStopRef[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.map(toStopRef).filter((s): s is MergePreviewStopRef => Boolean(s));
}

export function normalizeDuplicateGroups(preview: MergeTripPreviewResponse): MergePreviewStopRef[][] {
  const out: MergePreviewStopRef[][] = [];
  const duplicateKeys = [
    'duplicate_stops',
    'duplicate_stop_groups',
    'duplicate_trip_stops',
    'location_conflicts',
    'location_duplicates',
    'conflicts',
  ];

  duplicateKeys.forEach(key => {
    const raw = getCandidate(preview, key);
    if (!Array.isArray(raw) || raw.length === 0) {
      return;
    }

    const first = raw[0] as unknown;

    if (Array.isArray(first)) {
      raw.forEach(group => {
        const parsed = toStopGroup(group);
        if (parsed.length > 0) {
          out.push(parsed);
        }
      });
      return;
    }

    const plainGroup = toStopGroup(raw);
    if (plainGroup.length > 0) {
      out.push(plainGroup);
      return;
    }

    raw.forEach(item => {
      if (!isRecord(item)) {
        return;
      }
      const type = typeof item.type === 'string' ? item.type.toLowerCase() : '';
      if (type && !type.includes('duplicate')) {
        return;
      }

      const group: MergePreviewStopRef[] = [];
      if (Array.isArray(item.stops)) {
        group.push(...toStopGroup(item.stops));
      }
      const stopA = toStopRef(item.stop_a);
      const stopB = toStopRef(item.stop_b);
      const startStop = toStopRef(item.start_stop);
      const endStop = toStopRef(item.end_stop);

      if (stopA) group.push(stopA);
      if (stopB) group.push(stopB);
      if (startStop) group.push(startStop);
      if (endStop) group.push(endStop);

      if (group.length > 0) {
        out.push(group);
      }
    });
  });

  const seen = new Set<string>();
  return out.filter(group => {
    const signature = group
      .map(s => s.id)
      .sort((a, b) => a - b)
      .join(',');
    if (!signature || seen.has(signature)) {
      return false;
    }
    seen.add(signature);
    return true;
  });
}

function toOverlapItem(value: unknown): MergePreviewOverlapItem | null {
  if (!isRecord(value)) {
    return null;
  }

  const stopA = toStopRef(value.stop_a) ?? toStopRef(value.start_stop);
  const stopB = toStopRef(value.stop_b) ?? toStopRef(value.end_stop);
  const stops = Array.isArray(value.stops) ? toStopGroup(value.stops) : undefined;
  const stopIds = Array.isArray(value.stop_ids)
    ? value.stop_ids.filter((id): id is number => typeof id === 'number')
    : undefined;
  const message = typeof value.message === 'string' ? value.message : undefined;

  if (!stopA && !stopB && (!stops || stops.length === 0) && (!stopIds || stopIds.length === 0) && !message) {
    return null;
  }

  return {
    ...(stopA ? { stop_a: stopA } : {}),
    ...(stopB ? { stop_b: stopB } : {}),
    ...(stops && stops.length > 0 ? { stops } : {}),
    ...(stopIds && stopIds.length > 0 ? { stop_ids: stopIds } : {}),
    ...(message ? { message } : {}),
  };
}

export function normalizeOverlaps(preview: MergeTripPreviewResponse): MergePreviewOverlapItem[] {
  const overlapKeys = ['overlapping_time_ranges', 'overlapping_stops', 'time_overlaps', 'overlaps', 'conflicts'];
  const out: MergePreviewOverlapItem[] = [];

  overlapKeys.forEach(key => {
    const raw = getCandidate(preview, key);
    if (!Array.isArray(raw)) {
      return;
    }
    raw.forEach(item => {
      if (Array.isArray(item)) {
        const stops = toStopGroup(item);
        if (stops.length > 0) {
          out.push({ stops });
        }
        return;
      }
      if (isRecord(item) && typeof item.type === 'string') {
        const type = item.type.toLowerCase();
        const isOverlapConflict =
          type.includes('overlap') || type === 'overlapping_stop' || type.includes('time_overlap');
        if (!isOverlapConflict) {
          return;
        }
      }

      const overlap = toOverlapItem(item);
      if (overlap) {
        out.push(overlap);
      }
    });
  });

  return out;
}

export function dedupeOverlapStops(overlaps: MergePreviewOverlapItem[]): MergePreviewStopRef[] {
  const map = new Map<number, MergePreviewStopRef>();

  const add = (stop: MergePreviewStopRef | null | undefined) => {
    if (!stop || typeof stop.id !== 'number') {
      return;
    }
    const prev = map.get(stop.id);
    map.set(stop.id, prev ? ({ ...prev, ...stop, id: stop.id } as MergePreviewStopRef) : { ...stop });
  };

  overlaps.forEach(ov => {
    add(ov.stop_a);
    add(ov.stop_b);
    ov.stops?.forEach(s => add(s));
    ov.stop_ids?.forEach(id => add({ id }));
  });

  return Array.from(map.values()).sort((a, b) => a.id - b.id);
}

export function normalizeReminderConflicts(preview: MergeTripPreviewResponse): MergePreviewReminderConflict[] {
  const reminderKeys = ['duplicate_reminders', 'reminder_conflicts', 'reminders', 'conflicts'];
  const out: MergePreviewReminderConflict[] = [];

  reminderKeys.forEach(key => {
    const raw = getCandidate(preview, key);
    if (!Array.isArray(raw)) {
      return;
    }
    raw.forEach(item => {
      if (typeof item === 'string') {
        out.push({ message: item });
        return;
      }
      if (isRecord(item)) {
        const type = typeof item.type === 'string' ? item.type.toLowerCase() : '';
        if (key === 'conflicts' && type && !type.includes('reminder')) {
          return;
        }
        out.push(item as MergePreviewReminderConflict);
      }
    });
  });

  return out;
}

function hasTruthyConflictFlag(preview: MergeTripPreviewResponse): boolean {
  if (preview.has_conflicts === true || preview.has_conflicts === 1) {
    return true;
  }
  if (preview.conflicts === true || preview.conflicts === 1) {
    return true;
  }
  if (Array.isArray(preview.conflicts)) {
    return preview.conflicts.length > 0;
  }
  if (isRecord(preview.conflicts)) {
    return true;
  }
  const container = getConflictsContainer(preview);
  if (container && Object.keys(container).length > 0) {
    return true;
  }
  return false;
}

function hasExplicitNoConflict(preview: MergeTripPreviewResponse): boolean {
  return preview.has_conflicts === false || preview.conflicts === false;
}

export type MergeConflictWizardStep =
  | {
      kind: 'pair';
      left: MergePreviewStopRef;
      right: MergePreviewStopRef;
      message?: string;
    }
  | {
      kind: 'reminders';
      items: MergePreviewReminderConflict[];
    };

export function buildConflictWizardSteps(preview: MergeTripPreviewResponse): MergeConflictWizardStep[] {
  const steps: MergeConflictWizardStep[] = [];

  const duplicateGroups = normalizeDuplicateGroups(preview);
  duplicateGroups.forEach(group => {
    if (group.length < 2) return;
    const anchor = group[0];
    for (let i = 1; i < group.length; i += 1) {
      const candidate = group[i];
      if (anchor.id === candidate.id) continue;
      steps.push({ kind: 'pair', left: anchor, right: candidate });
    }
  });

  const overlaps = normalizeOverlaps(preview);
  overlaps.forEach(ov => {
    const left = ov.stop_a ?? ov.stops?.[0];
    const right = ov.stop_b ?? ov.stops?.[1];
    if (left && right && left.id !== right.id) {
      steps.push({
        kind: 'pair',
        left,
        right,
        ...(ov.message ? { message: ov.message } : {}),
      });
    }
  });

  const reminders = normalizeReminderConflicts(preview);
  if (reminders.length > 0) {
    steps.push({ kind: 'reminders', items: reminders });
  }

  const pairKeys = new Set<string>();
  const deduped: MergeConflictWizardStep[] = [];
  steps.forEach(step => {
    if (step.kind !== 'pair') {
      deduped.push(step);
      return;
    }
    const key = [step.left.id, step.right.id].sort((a, b) => a - b).join('|');
    if (pairKeys.has(key)) {
      return;
    }
    pairKeys.add(key);
    deduped.push(step);
  });

  return deduped;
}

export function mergePreviewHasConflicts(preview: MergeTripPreviewResponse): boolean {
  if (hasTruthyConflictFlag(preview)) {
    return true;
  }

  const dup = normalizeDuplicateGroups(preview);
  if (dup.some(g => g.length > 0)) {
    return true;
  }

  if (normalizeOverlaps(preview).length > 0) {
    return true;
  }
  if (normalizeReminderConflicts(preview).length > 0) {
    return true;
  }

  if (hasExplicitNoConflict(preview)) {
    return false;
  }

  return false;
}

export function formatMergePreviewAddressLine(stop: MergePreviewStopRef): string {
  const loc = stop.location?.trim();
  if (loc) return loc;
  const raw = stop.address;
  if (raw == null || raw === '') return '';
  if (typeof raw === 'string') return raw.trim();
  if (
    typeof raw === 'object' &&
    raw !== null &&
    'name' in raw &&
    typeof (raw as { name?: unknown }).name === 'string'
  ) {
    return (raw as { name: string }).name.trim();
  }
  return '';
}

export type MergePreviewStopDetailLine = {
  kind: 'address' | 'booking' | 'place' | 'notes';
  text: string;
};

export function mergePreviewStopDetailLines(stop: MergePreviewStopRef): MergePreviewStopDetailLine[] {
  const lines: MergePreviewStopDetailLine[] = [];
  const addr = formatMergePreviewAddressLine(stop);
  if (addr) lines.push({ kind: 'address', text: addr });
  if (stop.booking?.trim()) lines.push({ kind: 'booking', text: `Booking ${stop.booking.trim()}` });
  if (stop.place?.trim()) lines.push({ kind: 'place', text: stop.place.trim() });
  if (stop.notes?.trim()) lines.push({ kind: 'notes', text: stop.notes.trim() });
  return lines;
}

export function formatMergePreviewStopLabel(stop: MergePreviewStopRef): string {
  const title = stop.title || stop.name;
  const type = stop.stop_type ? String(stop.stop_type).replace(/_/g, ' ') : '';
  const base = title || type || `Stop #${stop.id}`;
  const trip = stop.trip_name ? ` · ${stop.trip_name}` : '';
  const startRaw =
    stop.start ||
    stop.start_time ||
    stop.start_date_time ||
    stop.departure_date_time ||
    stop.check_in_date_time;
  const endRaw =
    stop.end ||
    stop.end_time ||
    stop.end_date_time ||
    stop.arrival_date_time ||
    stop.check_out_date_time;
  const when = startRaw && endRaw ? ` (${formatPreviewTime(startRaw)} – ${formatPreviewTime(endRaw)})` : '';
  const addrLine = formatMergePreviewAddressLine(stop);
  const where = addrLine ? ` · ${addrLine}` : '';
  return `${base}${trip}${when}${where}`;
}

function datetimeCompleteness(s: string): number {
  const t = s.trim();
  if (!t) return 0;
  const parsed = new Date(t);
  if (Number.isNaN(parsed.getTime())) return 0;
  if (/^\d{4}-\d{2}-\d{2}/.test(t)) {
    return t.includes('T') || /\d{4}-\d{2}-\d{2}\s/.test(t) ? 4 : 2;
  }
  if (/^\d{1,2}:\d{2}/.test(t)) return 1;
  return 3;
}

function composeDateTimeFromParts(dateStr?: string, timeStr?: string): string | undefined {
  const d = dateStr?.trim();
  const tm = timeStr?.trim();
  if (!d || !tm) return undefined;
  const timeNorm =
    tm.length === 5 && /^\d{2}:\d{2}$/.test(tm)
      ? `${tm}:00`
      : tm.length === 8 && /^\d{2}:\d{2}:\d{2}$/.test(tm)
        ? tm
        : tm;
  const candidate = `${d}T${timeNorm}`;
  const parsed = new Date(candidate);
  if (Number.isNaN(parsed.getTime())) return undefined;
  return parsed.toISOString();
}

function pickBetterDatetime(prev?: string, next?: string): string | undefined {
  if (!prev) return next;
  if (!next) return prev;
  const cp = datetimeCompleteness(prev);
  const cn = datetimeCompleteness(next);
  if (cn > cp) return next;
  if (cp > cn) return prev;
  return next;
}

const STOP_DATETIME_KEYS: (keyof MergePreviewStopRef)[] = [
  'start',
  'end',
  'start_time',
  'end_time',
  'start_date_time',
  'end_date_time',
  'departure_date_time',
  'arrival_date_time',
  'check_in_date_time',
  'check_out_date_time',
  'start_date',
  'end_date',
];

function mergeStopSnapshots(
  prev: MergePreviewStopRef | undefined,
  raw: MergePreviewStopRef
): MergePreviewStopRef {
  if (!prev) return { ...raw };
  const merged: MergePreviewStopRef = { ...prev, ...raw };
  STOP_DATETIME_KEYS.forEach(key => {
    const pv = prev[key];
    const rv = raw[key];
    if (typeof pv === 'string' && typeof rv === 'string' && pv !== rv) {
      const chosen = pickBetterDatetime(pv, rv);
      if (chosen !== undefined) {
        merged[key] = chosen as never;
      }
    }
  });
  return merged;
}

function bestStartCandidate(stop: MergePreviewStopRef): string | undefined {
  const composed = composeDateTimeFromParts(stop.start_date, stop.start_time);
  const candidates = [
    composed,
    stop.start,
    stop.start_date_time,
    stop.start_time,
    stop.departure_date_time,
    stop.check_in_date_time,
  ].filter((x): x is string => typeof x === 'string' && x.trim().length > 0);

  let best: string | undefined;
  let bestScore = -Infinity;
  for (const c of candidates) {
    const rank = datetimeCompleteness(c);
    const ts = new Date(c).getTime();
    if (Number.isNaN(ts)) continue;
    const score = rank * 1e15 + ts;
    if (score > bestScore) {
      bestScore = score;
      best = c;
    }
  }
  return best;
}

function bestEndCandidate(stop: MergePreviewStopRef): string | undefined {
  const endDate = stop.end_date ?? stop.start_date;
  const composed = composeDateTimeFromParts(endDate, stop.end_time);
  const candidates = [
    composed,
    stop.end,
    stop.end_date_time,
    stop.end_time,
    stop.arrival_date_time,
    stop.check_out_date_time,
  ].filter((x): x is string => typeof x === 'string' && x.trim().length > 0);

  let best: string | undefined;
  let bestScore = -Infinity;
  for (const c of candidates) {
    const rank = datetimeCompleteness(c);
    const ts = new Date(c).getTime();
    if (Number.isNaN(ts)) continue;
    const score = rank * 1e15 + ts;
    if (score > bestScore) {
      bestScore = score;
      best = c;
    }
  }
  return best;
}

export function extractStopTimeRange(stop: MergePreviewStopRef): { start?: string; end?: string } {
  const start = bestStartCandidate(stop);
  const end = bestEndCandidate(stop);
  return {
    ...(start ? { start } : {}),
    ...(end ? { end } : {}),
  };
}

function looksLikeMergePreviewStopRecord(value: UnknownRecord): boolean {
  return (
    typeof value.id === 'number' &&
    ('stop_type' in value ||
      'start' in value ||
      'end' in value ||
      'start_time' in value ||
      'end_time' in value ||
      'start_date_time' in value ||
      'end_date_time' in value ||
      'departure_date_time' in value ||
      'arrival_date_time' in value ||
      'check_in_date_time' in value ||
      'check_out_date_time' in value ||
      'start_date' in value ||
      'end_date' in value ||
      'booking' in value ||
      'place' in value ||
      'notes' in value ||
      'trip_name' in value)
  );
}

export function collectMergePreviewStopsById(preview: MergeTripPreviewResponse): Map<number, MergePreviewStopRef> {
  const map = new Map<number, MergePreviewStopRef>();

  const walk = (node: unknown): void => {
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    if (!isRecord(node)) return;

    if (looksLikeMergePreviewStopRecord(node)) {
      const raw = node as unknown as MergePreviewStopRef;
      map.set(raw.id, mergeStopSnapshots(map.get(raw.id), raw));
    }

    Object.keys(node).forEach(key => walk(node[key]));
  };

  walk(preview as unknown);
  return map;
}

function stopIntervalsOverlap(a: MergePreviewStopRef, b: MergePreviewStopRef): boolean {
  const ra = extractStopTimeRange(a);
  const rb = extractStopTimeRange(b);
  if (!ra.start || !ra.end || !rb.start || !rb.end) return false;
  const as = new Date(ra.start).getTime();
  const ae = new Date(ra.end).getTime();
  const bs = new Date(rb.start).getTime();
  const be = new Date(rb.end).getTime();
  if ([as, ae, bs, be].some(Number.isNaN)) return false;
  if (ae < as || be < bs) return false;
  return Math.max(as, bs) <= Math.min(ae, be);
}

export function findDerivedScheduleOverlapPairs(
  preview: MergeTripPreviewResponse,
  discardedStopIds: Iterable<number>
): Array<{ left: MergePreviewStopRef; right: MergePreviewStopRef }> {
  const discarded = new Set(discardedStopIds);
  const byId = collectMergePreviewStopsById(preview);
  discarded.forEach(id => byId.delete(id));

  const stops = Array.from(byId.values());
  const out: Array<{ left: MergePreviewStopRef; right: MergePreviewStopRef }> = [];

  for (let i = 0; i < stops.length; i++) {
    for (let j = i + 1; j < stops.length; j++) {
      const u = stops[i];
      const v = stops[j];
      if (u.id === v.id) continue;
      if (stopIntervalsOverlap(u, v)) {
        out.push({ left: u, right: v });
      }
    }
  }

  return out;
}

function overlapPairKey(leftId: number, rightId: number): string {
  return [leftId, rightId].sort((a, b) => a - b).join('|');
}

export function mergeDerivedOverlapStepsIntoWizard(
  preview: MergeTripPreviewResponse,
  discardedStopIds: Iterable<number>,
  baseSteps: MergeConflictWizardStep[]
): MergeConflictWizardStep[] {
  const derivedPairs = findDerivedScheduleOverlapPairs(preview, discardedStopIds);
  const seenKeys = new Set<string>();
  const out: MergeConflictWizardStep[] = [];

  for (const step of baseSteps) {
    if (step.kind === 'pair') {
      seenKeys.add(overlapPairKey(step.left.id, step.right.id));
    }
    out.push(step);
  }

  const remindersIdx = out.findIndex(s => s.kind === 'reminders');
  const insertAt = remindersIdx === -1 ? out.length : remindersIdx;
  const extras: MergeConflictWizardStep[] = [];

  for (const { left, right } of derivedPairs) {
    const key = overlapPairKey(left.id, right.id);
    if (seenKeys.has(key)) continue;
    seenKeys.add(key);
    extras.push({
      kind: 'pair',
      left,
      right,
      message: 'This overlap appeared after editing times — resolve before merging.',
    });
  }

  if (extras.length === 0) return out;
  return [...out.slice(0, insertAt), ...extras, ...out.slice(insertAt)];
}

function formatPreviewTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function toDateTimeLocal(isoLike?: string): string {
  if (!isoLike) return '';
  const d = new Date(isoLike);
  if (Number.isNaN(d.getTime())) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

export function fromDateTimeLocal(local: string): string {
  return new Date(local).toISOString();
}
